import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  RiAddLine,
  RiArrowRightLine,
  RiCalendarLine,
  RiExternalLinkLine,
  RiFileAddLine,
  RiLink,
  RiLogoutBoxRLine,
  RiSettings4Line,
  RiStore3Line,
} from "@remixicon/react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from "@/components/ui/command";
import { useAuth } from "@/contexts/AuthContext";
import {
  canSeeNavItem,
  settingsNavItem,
  visibleNavSections,
  type NavIcon,
} from "@/components/dashboard/nav-config";
import { useNavAccess } from "@/components/dashboard/use-nav-access";

type Action = {
  id: string;
  label: string;
  icon: NavIcon;
  keywords?: string[];
  run: () => void;
};

const settingsSections = [
  { id: "profile", label: "Profile", storeSetting: false },
  { id: "billing", label: "Billing", storeSetting: false },
  { id: "store", label: "Store details", storeSetting: true },
  { id: "checkout", label: "Checkout & payments", storeSetting: true },
  { id: "sales-link", label: "Sales link appearance", storeSetting: true },
  { id: "booking", label: "Booking settings", storeSetting: true },
  { id: "invoices", label: "Invoice settings", storeSetting: true },
  { id: "store-managers", label: "Store managers", storeSetting: true },
  { id: "account", label: "Account", storeSetting: false },
] as const;

const itemClass =
  "gap-3 rounded-xl px-3 py-2.5 text-[13.5px] font-medium data-[selected=true]:bg-[#F3F3F0] data-[selected=true]:text-foreground";

export function CommandMenu({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const { currentStore, logout } = useAuth();
  const access = useNavAccess();
  const sections = visibleNavSections(access);
  const storeLink = currentStore?.url ? `https://sqale.shop/${currentStore.url}` : "";
  const canEditStore = access.isOwner || access.hasPermission("settings");

  const run = (fn: () => void) => {
    onOpenChange(false);
    fn();
  };

  const actions = useMemo<Action[]>(() => {
    const list: Action[] = [];
    if (access.hasPermission("products")) {
      list.push({
        id: "new-product",
        label: "Add a product",
        icon: RiAddLine,
        keywords: ["create", "new item"],
        run: () => navigate("/dashboard/products/new"),
      });
    }
    if (access.hasPermission("invoices")) {
      list.push({
        id: "new-invoice",
        label: "Create an invoice",
        icon: RiFileAddLine,
        keywords: ["bill", "new"],
        run: () => navigate("/dashboard/invoices/new"),
      });
    }
    if (access.hasPermission("pos")) {
      list.push({
        id: "pos",
        label: "Record a walk-in sale",
        icon: RiStore3Line,
        keywords: ["pos", "point of sale"],
        run: () => navigate("/dashboard/pos"),
      });
    }
    if (access.bookingsEnabled && access.hasPermission("bookings")) {
      list.push({
        id: "new-slot",
        label: "Create a booking slot",
        icon: RiCalendarLine,
        keywords: ["service", "appointment"],
        run: () => navigate("/dashboard/booking-slots/new"),
      });
    }
    if (storeLink) {
      list.push(
        {
          id: "copy-link",
          label: "Copy store link",
          icon: RiLink,
          keywords: ["share", "url"],
          run: () => {
            navigator.clipboard
              .writeText(storeLink)
              .then(() => toast.success("Store link copied"))
              .catch(() => toast.error("Couldn't copy the link"));
          },
        },
        {
          id: "visit-store",
          label: "View live store",
          icon: RiExternalLinkLine,
          keywords: ["open", "storefront", "preview"],
          run: () => window.open(storeLink, "_blank", "noopener"),
        },
      );
    }
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeLink, access.isOwner, access.bookingsEnabled, access.isPaid, currentStore?._id]);

  const visibleSettings = settingsSections.filter((section) => {
    if (section.id === "billing") return access.isOwner;
    if (section.id === "store-managers") return access.isOwner && access.isPaid;
    if (section.id === "booking" && !canEditStore) return false;
    return section.storeSetting ? canEditStore : true;
  });

  const footerNav = [settingsNavItem].filter((item) =>
    canSeeNavItem(item, access),
  );

  return (
    <CommandDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Search"
      description="Jump to a page or run an action"
    >
      <CommandInput placeholder="Search pages, settings, and actions…" />
      <CommandList className="max-h-[min(60vh,440px)] px-1.5 pb-2">
        <CommandEmpty>
          <p className="text-sm font-semibold text-foreground">No matches</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Try a page name like “orders” or “inventory”.
          </p>
        </CommandEmpty>

        {actions.length > 0 && (
          <CommandGroup heading="Quick actions">
            {actions.map((action) => (
              <CommandItem
                key={action.id}
                value={`${action.label} ${(action.keywords ?? []).join(" ")}`}
                onSelect={() => run(action.run)}
                className={itemClass}
              >
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#EDE4FB] text-primary">
                  <action.icon className="h-4 w-4" />
                </span>
                {action.label}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        <CommandSeparator className="my-1" />

        <CommandGroup heading="Go to">
          {[...sections.flatMap((section) => section.items), ...footerNav].map((item) => (
            <CommandItem
              key={item.href}
              value={`${item.name} ${(item.keywords ?? []).join(" ")}`}
              onSelect={() => run(() => navigate(item.href))}
              className={itemClass}
            >
              <span className="flex h-7 w-7 items-center justify-center rounded-lg border border-[#EBEBEB] bg-white text-[#6B6B66]">
                <item.icon className="h-4 w-4" />
              </span>
              {item.name}
              <CommandShortcut>
                <RiArrowRightLine className="h-3.5 w-3.5 text-muted-foreground" />
              </CommandShortcut>
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator className="my-1" />

        <CommandGroup heading="Settings">
          {visibleSettings.map((section) => (
            <CommandItem
              key={section.id}
              value={section.id === "sales-link" ? "settings customization colors banner hero sales link appearance" : `settings ${section.label}`}
              onSelect={() => run(() => navigate(section.id === "sales-link" ? "/dashboard/sales-link" : `/dashboard/settings#${section.id}`))}
              className={itemClass}
            >
              <span className="flex h-7 w-7 items-center justify-center rounded-lg border border-[#EBEBEB] bg-white text-[#6B6B66]">
                <RiSettings4Line className="h-4 w-4" />
              </span>
              {section.label}
            </CommandItem>
          ))}
          <CommandItem
            value="log out sign out logout"
            onSelect={() => run(logout)}
            className={itemClass}
          >
            <span className="flex h-7 w-7 items-center justify-center rounded-lg border border-[#EBEBEB] bg-white text-red-600">
              <RiLogoutBoxRLine className="h-4 w-4" />
            </span>
            Log out
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
